import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SEO, SEO_CONFIG } from '@/components/ui/SEO';
import {
  Mail,
  MapPin,
  Send,
  MessageSquare,
  Building2,
  Headphones,
  FileText,
  CheckCircle,
  AlertCircle,
  Clock,
  Globe,
  Linkedin,
  Twitter,
  Github,
  Sparkles,
  Users,
  Zap,
  Shield,
  User,
  Briefcase,
} from 'lucide-react';

// Icon mappings
const reasonIcons: Record<string, typeof FileText> = {
  project: FileText,
  partnership: Users,
  support: Headphones,
  enterprise: Building2,
  careers: Briefcase,
  other: MessageSquare,
};

const contactInfoIcons: Record<string, typeof Mail> = {
  general: Mail,
  support: Headphones,
  address: MapPin,
};

export function ContactPage() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    reason: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Translated contact reasons
  const reasonKeys = ['project', 'partnership', 'support', 'enterprise', 'careers', 'other'];
  const contactReasons = reasonKeys.map((key) => ({
    value: key,
    label: t(`contact.reasons.${key}`),
    icon: reasonIcons[key],
  }));

  // Translated contact info
  const contactInfoKeys = ['general', 'support', 'address'];
  const contactInfoData: Record<string, { value: string; href: string }> = {
    general: { value: 'hello@bosphoruslabs.com', href: 'mailto:hello@bosphoruslabs.com' },
    support: { value: 'support@bosphoruslabs.com', href: 'mailto:support@bosphoruslabs.com' },
    address: { value: 'İstanbul, Türkiye', href: 'https://maps.google.com/?q=Istanbul,Turkey' },
  };
  const contactInfo = contactInfoKeys.map((key) => ({
    icon: contactInfoIcons[key],
    title: t(`contact.contactInfo.${key}.title`),
    value: contactInfoData[key].value,
    href: contactInfoData[key].href,
    description: t(`contact.contactInfo.${key}.description`),
  }));

  const socialLinks = [
    { icon: Linkedin, href: 'https://linkedin.com/company/bosphoruslabs', label: 'LinkedIn' },
    { icon: Twitter, href: 'https://twitter.com/bosphoruslabs', label: 'Twitter' },
    { icon: Github, href: 'https://github.com/bosphoruslabs', label: 'GitHub' },
  ];

  // Translated features
  const featureKeys = ['fastResponse', 'privacy', 'globalService'];
  const featureIcons: Record<string, typeof Zap> = {
    fastResponse: Zap,
    privacy: Shield,
    globalService: Globe,
  };
  const features = featureKeys.map((key) => ({
    icon: featureIcons[key],
    title: t(`contact.features.${key}.title`),
    description: t(`contact.features.${key}.description`),
  }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message || !formData.reason) {
      setError(t('contact.form.fillRequired'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        company: '',
        reason: '',
        message: '',
      });
    } catch {
      setError(t('contact.form.sendError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO {...SEO_CONFIG.contact} />
      <div className="min-h-screen">
      {/* Hero Section */}
      <section className="section-lg relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-accent/5 via-transparent to-transparent" />

          {/* Floating Orbs */}
          <div className="absolute top-20 left-[10%] w-72 h-72 bg-accent/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 right-[10%] w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cyan-500/5 rounded-full blur-3xl" />

          {/* Grid Pattern */}
          <div
            className="absolute inset-0 opacity-[0.02]"
            style={{
              backgroundImage: `linear-gradient(rgba(139, 92, 246, 0.3) 1px, transparent 1px),
                               linear-gradient(90deg, rgba(139, 92, 246, 0.3) 1px, transparent 1px)`,
              backgroundSize: '60px 60px'
            }}
          />

          {/* Decorative Lines */}
          <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-transparent via-accent/20 to-transparent" />
          <div className="absolute top-0 right-1/4 w-px h-full bg-gradient-to-b from-transparent via-purple-500/20 to-transparent" />
        </div>

        <div className="container relative">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 border border-accent/20 text-accent text-sm mb-6">
              <MessageSquare size={16} />
              <span>{t('contact.hero.badge')}</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              {t('contact.hero.title')}{' '}
              <span className="bg-gradient-to-r from-accent via-purple-500 to-cyan-500 bg-clip-text text-transparent">
                {t('contact.hero.titleHighlight')}
              </span>
            </h1>

            <p className="text-lg md:text-xl text-text-secondary max-w-2xl mx-auto mb-8">
              {t('contact.hero.description')}
            </p>

            {/* Quick Stats */}
            <div className="flex flex-wrap justify-center gap-6 md:gap-12">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
                    <feature.icon className="text-accent" size={20} />
                  </div>
                  <div className="text-left">
                    <div className="font-semibold text-sm">{feature.title}</div>
                    <div className="text-text-secondary text-xs">{feature.description}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="section">
        <div className="container">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Info Sidebar */}
            <div className="lg:col-span-2 space-y-8">
              {/* Contact Cards */}
              <div className="space-y-4">
                {contactInfo.map((item, index) => (
                  <a
                    key={index}
                    href={item.href}
                    target={item.href.startsWith('http') ? '_blank' : undefined}
                    rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className="group flex items-start gap-4 p-5 rounded-2xl bg-bg-surface border border-border-default hover:border-accent/50 transition-all duration-300 hover:-translate-y-1"
                  >
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent/20 to-purple-500/10 flex items-center justify-center group-hover:from-accent/30 group-hover:to-purple-500/20 transition-colors">
                      <item.icon className="text-accent" size={24} />
                    </div>
                    <div>
                      <p className="text-text-secondary text-sm">{item.title}</p>
                      <p className="font-semibold group-hover:text-accent transition-colors">{item.value}</p>
                      <p className="text-text-tertiary text-xs mt-1">{item.description}</p>
                    </div>
                  </a>
                ))}
              </div>

              {/* Working Hours */}
              <div className="p-5 rounded-2xl bg-bg-surface border border-border-default">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center">
                    <Clock className="text-green-400" size={20} />
                  </div>
                  <h3 className="font-semibold">{t('contact.workingHours.title')}</h3>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-text-secondary">{t('contact.workingHours.weekdays')}</span>
                    <span className="font-medium">09:00 - 18:00</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-secondary">{t('contact.workingHours.saturday')}</span>
                    <span className="font-medium">10:00 - 14:00</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-secondary">{t('contact.workingHours.sunday')}</span>
                    <span className="text-text-tertiary">{t('contact.workingHours.closed')}</span>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="p-5 rounded-2xl bg-bg-surface border border-border-default">
                <h3 className="font-semibold mb-4">{t('contact.socialMedia')}</h3>
                <div className="flex gap-3">
                  {socialLinks.map((social, index) => (
                    <a
                      key={index}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 rounded-xl bg-bg-primary border border-border-default flex items-center justify-center hover:border-accent hover:text-accent transition-colors"
                      title={social.label}
                    >
                      <social.icon size={20} />
                    </a>
                  ))}
                </div>
              </div>

              {/* Quick Help */}
              <div className="p-5 rounded-2xl bg-gradient-to-br from-accent/10 via-purple-500/5 to-cyan-500/10 border border-accent/20">
                <div className="flex items-center gap-3 mb-3">
                  <Sparkles className="text-accent" size={20} />
                  <h3 className="font-semibold">{t('contact.quickHelp.title')}</h3>
                </div>
                <p className="text-text-secondary text-sm mb-4">
                  {t('contact.quickHelp.description')}
                </p>
                <div className="flex gap-2">
                  <Link to="/about" className="btn btn-secondary btn-sm flex-1 text-center justify-center">
                    {t('contact.quickHelp.about')}
                  </Link>
                  <Link to="/blog" className="btn btn-ghost btn-sm flex-1 text-center justify-center">
                    {t('contact.quickHelp.blog')}
                  </Link>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              <div className="card p-6 md:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent to-purple-600 flex items-center justify-center">
                    <Send className="text-white" size={24} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">{t('contact.form.title')}</h2>
                    <p className="text-text-secondary text-sm">{t('contact.form.subtitle')}</p>
                  </div>
                </div>

                {isSubmitted ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="text-green-400" size={40} />
                    </div>
                    <h3 className="text-2xl font-bold mb-3">{t('contact.form.successTitle')}</h3>
                    <p className="text-text-secondary mb-8 max-w-md mx-auto">
                      {t('contact.form.successMessage')}
                    </p>
                    <button
                      onClick={() => setIsSubmitted(false)}
                      className="btn btn-secondary"
                    >
                      {t('contact.form.sendAnother')}
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    {error && (
                      <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 flex items-start gap-3">
                        <AlertCircle className="text-red-400 flex-shrink-0 mt-0.5" size={18} />
                        <p className="text-sm text-red-400">{error}</p>
                      </div>
                    )}

                    {/* Name & Email */}
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          <User size={14} className="inline mr-2" />
                          {t('contact.form.name.label')} <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder={t('contact.form.name.placeholder')}
                          className="w-full px-4 py-3 rounded-xl bg-bg-surface border border-border-default focus:border-accent focus:outline-none transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          <Mail size={14} className="inline mr-2" />
                          {t('contact.form.email.label')} <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder={t('contact.form.email.placeholder')}
                          className="w-full px-4 py-3 rounded-xl bg-bg-surface border border-border-default focus:border-accent focus:outline-none transition-colors"
                        />
                      </div>
                    </div>

                    {/* Company */}
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        <Building2 size={14} className="inline mr-2" />
                        {t('contact.form.company.label')}
                      </label>
                      <input
                        type="text"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        placeholder={t('contact.form.company.placeholder')}
                        className="w-full px-4 py-3 rounded-xl bg-bg-surface border border-border-default focus:border-accent focus:outline-none transition-colors"
                      />
                    </div>

                    {/* Reason */}
                    <div>
                      <label className="block text-sm font-medium mb-3">
                        <MessageSquare size={14} className="inline mr-2" />
                        {t('contact.form.reason.label')} <span className="text-red-400">*</span>
                      </label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {contactReasons.map((reason) => (
                          <button
                            key={reason.value}
                            type="button"
                            onClick={() => setFormData({ ...formData, reason: reason.value })}
                            className={`flex items-center gap-2 p-3 rounded-xl border transition-all ${
                              formData.reason === reason.value
                                ? 'bg-accent/10 border-accent text-accent'
                                : 'bg-bg-surface border-border-default hover:border-accent/50 text-text-secondary hover:text-text-primary'
                            }`}
                          >
                            <reason.icon size={16} />
                            <span className="text-sm font-medium">{reason.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Message */}
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        <FileText size={14} className="inline mr-2" />
                        {t('contact.form.message.label')} <span className="text-red-400">*</span>
                      </label>
                      <textarea
                        required
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        rows={5}
                        placeholder={t('contact.form.message.placeholder')}
                        className="w-full px-4 py-3 rounded-xl bg-bg-surface border border-border-default focus:border-accent focus:outline-none transition-colors resize-none"
                      />
                    </div>

                    {/* Privacy Note */}
                    <p className="text-text-tertiary text-xs">
                      {t('contact.form.privacy')}{' '}
                      <Link to="/privacy" className="text-accent hover:underline">
                        {t('contact.form.privacyPolicy')}
                      </Link>
                      .
                    </p>

                    {/* Submit */}
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full btn btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          {t('contact.form.sending')}
                        </>
                      ) : (
                        <>
                          {t('contact.form.send')}
                          <Send size={18} />
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map / Location Section */}
      <section className="section bg-bg-surface">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Map Placeholder */}
            <div className="relative aspect-video lg:aspect-square rounded-3xl overflow-hidden bg-bg-primary border border-border-default">
              {/* Decorative Map Background */}
              <div className="absolute inset-0 opacity-30">
                {/* Radial gradient - pulsing */}
                <div
                  className="absolute inset-0 animate-pulse"
                  style={{
                    backgroundImage: `radial-gradient(circle at 50% 50%, rgba(139, 92, 246, 0.4) 0%, transparent 50%)`,
                    animationDuration: '3s'
                  }}
                />
                {/* Grid pattern */}
                <div
                  className="absolute inset-0"
                  style={{
                    backgroundImage: `linear-gradient(rgba(139, 92, 246, 0.15) 1px, transparent 1px),
                                     linear-gradient(90deg, rgba(139, 92, 246, 0.15) 1px, transparent 1px)`,
                    backgroundSize: '30px 30px'
                  }}
                />
              </div>

              {/* Animated Connection Lines */}
              <svg className="absolute inset-0 w-full h-full opacity-50">
                <defs>
                  <linearGradient id="contact-line-grad-1" x1="50%" y1="50%" x2="0%" y2="0%">
                    <stop offset="0%" stopColor="#8B5CF6" stopOpacity="0.8" />
                    <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0.1" />
                  </linearGradient>
                  <linearGradient id="contact-line-grad-2" x1="50%" y1="50%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#8B5CF6" stopOpacity="0.8" />
                    <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0.1" />
                  </linearGradient>
                </defs>
                {/* Lines to each country flag */}
                <line x1="50%" y1="50%" x2="15%" y2="25%" stroke="url(#contact-line-grad-1)" strokeWidth="1.5" className="animate-pulse" />
                <line x1="50%" y1="50%" x2="50%" y2="10%" stroke="url(#contact-line-grad-1)" strokeWidth="1.5" className="animate-pulse" style={{ animationDelay: '0.2s' }} />
                <line x1="50%" y1="50%" x2="85%" y2="25%" stroke="url(#contact-line-grad-2)" strokeWidth="1.5" className="animate-pulse" style={{ animationDelay: '0.4s' }} />
                <line x1="50%" y1="50%" x2="90%" y2="50%" stroke="url(#contact-line-grad-2)" strokeWidth="1.5" className="animate-pulse" style={{ animationDelay: '0.6s' }} />
                <line x1="50%" y1="50%" x2="85%" y2="75%" stroke="url(#contact-line-grad-2)" strokeWidth="1.5" className="animate-pulse" style={{ animationDelay: '0.8s' }} />
                <line x1="50%" y1="50%" x2="50%" y2="90%" stroke="url(#contact-line-grad-1)" strokeWidth="1.5" className="animate-pulse" style={{ animationDelay: '1s' }} />
                <line x1="50%" y1="50%" x2="15%" y2="75%" stroke="url(#contact-line-grad-1)" strokeWidth="1.5" className="animate-pulse" style={{ animationDelay: '1.2s' }} />
                <line x1="50%" y1="50%" x2="10%" y2="50%" stroke="url(#contact-line-grad-1)" strokeWidth="1.5" className="animate-pulse" style={{ animationDelay: '1.4s' }} />
              </svg>

              {/* Country Flags - Circular arrangement */}
              {/* UK - Top Left */}
              <div className="absolute top-[20%] left-[10%] animate-bounce" style={{ animationDuration: '3s', animationDelay: '0s' }}>
                <div className="relative group">
                  <div className="absolute inset-0 bg-accent/30 rounded-full blur-md group-hover:bg-accent/50 transition-all" />
                  <img src="https://hatscripts.github.io/circle-flags/flags/gb.svg" alt="UK" className="relative w-10 h-10 rounded-full shadow-lg border-2 border-white/20" />
                </div>
              </div>

              {/* France - Top */}
              <div className="absolute top-[5%] left-[45%] animate-bounce" style={{ animationDuration: '3.2s', animationDelay: '0.2s' }}>
                <div className="relative group">
                  <div className="absolute inset-0 bg-accent/30 rounded-full blur-md group-hover:bg-accent/50 transition-all" />
                  <img src="https://hatscripts.github.io/circle-flags/flags/fr.svg" alt="France" className="relative w-10 h-10 rounded-full shadow-lg border-2 border-white/20" />
                </div>
              </div>

              {/* USA - Top Right */}
              <div className="absolute top-[20%] right-[10%] animate-bounce" style={{ animationDuration: '2.8s', animationDelay: '0.4s' }}>
                <div className="relative group">
                  <div className="absolute inset-0 bg-accent/30 rounded-full blur-md group-hover:bg-accent/50 transition-all" />
                  <img src="https://hatscripts.github.io/circle-flags/flags/us.svg" alt="USA" className="relative w-10 h-10 rounded-full shadow-lg border-2 border-white/20" />
                </div>
              </div>

              {/* Canada - Right */}
              <div className="absolute top-[45%] right-[5%] animate-bounce" style={{ animationDuration: '3.5s', animationDelay: '0.6s' }}>
                <div className="relative group">
                  <div className="absolute inset-0 bg-accent/30 rounded-full blur-md group-hover:bg-accent/50 transition-all" />
                  <img src="https://hatscripts.github.io/circle-flags/flags/ca.svg" alt="Canada" className="relative w-10 h-10 rounded-full shadow-lg border-2 border-white/20" />
                </div>
              </div>

              {/* Mexico - Bottom Right */}
              <div className="absolute bottom-[20%] right-[10%] animate-bounce" style={{ animationDuration: '3.3s', animationDelay: '0.8s' }}>
                <div className="relative group">
                  <div className="absolute inset-0 bg-accent/30 rounded-full blur-md group-hover:bg-accent/50 transition-all" />
                  <img src="https://hatscripts.github.io/circle-flags/flags/mx.svg" alt="Mexico" className="relative w-10 h-10 rounded-full shadow-lg border-2 border-white/20" />
                </div>
              </div>

              {/* Morocco - Bottom */}
              <div className="absolute bottom-[5%] left-[45%] animate-bounce" style={{ animationDuration: '2.9s', animationDelay: '1s' }}>
                <div className="relative group">
                  <div className="absolute inset-0 bg-accent/30 rounded-full blur-md group-hover:bg-accent/50 transition-all" />
                  <img src="https://hatscripts.github.io/circle-flags/flags/ma.svg" alt="Morocco" className="relative w-10 h-10 rounded-full shadow-lg border-2 border-white/20" />
                </div>
              </div>

              {/* Azerbaijan - Bottom Left */}
              <div className="absolute bottom-[20%] left-[10%] animate-bounce" style={{ animationDuration: '3.1s', animationDelay: '1.2s' }}>
                <div className="relative group">
                  <div className="absolute inset-0 bg-accent/30 rounded-full blur-md group-hover:bg-accent/50 transition-all" />
                  <img src="https://hatscripts.github.io/circle-flags/flags/az.svg" alt="Azerbaijan" className="relative w-10 h-10 rounded-full shadow-lg border-2 border-white/20" />
                </div>
              </div>

              {/* Cyprus - Left */}
              <div className="absolute top-[45%] left-[5%] animate-bounce" style={{ animationDuration: '3.4s', animationDelay: '1.4s' }}>
                <div className="relative group">
                  <div className="absolute inset-0 bg-accent/30 rounded-full blur-md group-hover:bg-accent/50 transition-all" />
                  <img src="https://hatscripts.github.io/circle-flags/flags/cy.svg" alt="Cyprus" className="relative w-10 h-10 rounded-full shadow-lg border-2 border-white/20" />
                </div>
              </div>

              {/* Orbiting circles */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="absolute w-40 h-40 border border-accent/20 rounded-full animate-spin" style={{ animationDuration: '25s' }} />
                <div className="absolute w-56 h-56 border border-purple-500/15 rounded-full animate-spin" style={{ animationDuration: '35s', animationDirection: 'reverse' }} />
                <div className="absolute w-72 h-72 border border-accent/10 rounded-full animate-spin" style={{ animationDuration: '45s' }} />
              </div>

              {/* Turkey Flag - Center */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative">
                  <div className="absolute -inset-8 bg-red-500/20 rounded-full animate-ping" style={{ animationDuration: '2s' }} />
                  <div className="absolute -inset-4 bg-red-500/10 rounded-full animate-pulse" style={{ animationDuration: '1.5s' }} />
                  <div className="relative w-24 h-24 rounded-full shadow-2xl shadow-red-500/30 border-4 border-white/30 overflow-hidden">
                    <img src="https://hatscripts.github.io/circle-flags/flags/tr.svg" alt="Turkey" className="w-full h-full" />
                  </div>
                </div>
              </div>

              {/* Location Label */}
              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-bg-surface/90 backdrop-blur-sm rounded-xl p-4 border border-border-default">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                      <Building2 className="text-accent" size={20} />
                    </div>
                    <div>
                      <p className="font-semibold">{t('contact.location.headquarters')}</p>
                      <p className="text-text-secondary text-sm">İstanbul, Türkiye</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Info */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 text-accent text-sm mb-4">
                <Globe size={14} />
                <span>{t('contact.location.badge')}</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                {t('contact.location.title')}{' '}
                <span className="text-accent">{t('contact.location.titleHighlight')}</span>
              </h2>
              <p className="text-text-secondary text-lg mb-8">
                {t('contact.location.description')}
              </p>

              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 rounded-xl bg-bg-primary border border-border-default">
                  <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                    <Globe className="text-blue-400" size={24} />
                  </div>
                  <div>
                    <p className="font-semibold">{t('contact.location.activeCountries')}</p>
                    <p className="text-text-secondary text-sm">{t('contact.location.countriesList')}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 rounded-xl bg-bg-primary border border-border-default">
                  <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
                    <Users className="text-green-400" size={24} />
                  </div>
                  <div>
                    <p className="font-semibold">{t('contact.location.remoteCollaboration.title')}</p>
                    <p className="text-text-secondary text-sm">{t('contact.location.remoteCollaboration.description')}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 rounded-xl bg-bg-primary border border-border-default">
                  <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
                    <Clock className="text-purple-400" size={24} />
                  </div>
                  <div>
                    <p className="font-semibold">{t('contact.location.flexibleTimezones.title')}</p>
                    <p className="text-text-secondary text-sm">{t('contact.location.flexibleTimezones.description')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-accent/10 via-purple-500/5 to-cyan-500/10 border border-accent/20 p-8 md:p-12">
              {/* Background */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-accent/10 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-purple-500/10 rounded-full blur-3xl" />

              <div className="relative text-center">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-accent to-purple-600 flex items-center justify-center mx-auto mb-6">
                  <Headphones className="text-white" size={32} />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold mb-4">
                  {t('contact.cta.title')}
                </h2>
                <p className="text-text-secondary mb-8 max-w-xl mx-auto">
                  {t('contact.cta.description')}
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <a href="mailto:support@bosphoruslabs.com" className="btn btn-primary">
                    <Mail size={18} />
                    {t('contact.cta.supportEmail')}
                  </a>
                  <a href="mailto:hello@bosphoruslabs.com" className="btn btn-secondary">
                    <MessageSquare size={18} />
                    {t('contact.cta.generalContact')}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      </div>
    </>
  );
}
